class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.summary = this.mainDetailsToggle.querySelector('summary');
    this.parentLink = this.summary.querySelector('.header__menu-parent-link');

    // Open the dropdown on hover with a mouse/trackpad only (checked per event, so touchscreen
    // laptops still get tap-to-open). A short close delay lets the cursor travel across the gap
    // between the menu title and the dropdown panel.
    this.addEventListener('pointerenter', this.onPointerEnter.bind(this));
    this.addEventListener('pointerleave', this.onPointerLeave.bind(this));
    this.summary.addEventListener('pointerdown', (event) => (this.lastPointerType = event.pointerType));
    this.summary.addEventListener('click', this.onSummaryClick.bind(this));

    // With touch or pen there is no hover, so the first tap on a parent link opens the
    // dropdown and a second tap follows the link.
    if (this.parentLink) this.parentLink.addEventListener('click', this.onParentLinkClick.bind(this));

    // Short (single-column) menus line their links up under the parent item.
    this.alignedList = this.content.querySelector('[data-align-under-parent]');
    if (this.alignedList) {
      window.addEventListener('resize', () => {
        if (this.mainDetailsToggle.open) this.alignUnderParent();
      });
    }
  }

  alignUnderParent() {
    const list = this.alignedList;
    list.style.paddingLeft = '';
    const styles = getComputedStyle(list);
    const basePadding = parseFloat(styles.paddingLeft);
    const listRect = list.getBoundingClientRect();
    const parentText = this.parentLink || this.summary.querySelector('span') || this.summary;
    // Keep at least 30rem (~300px) of room for the links so they never run off the right edge.
    const maxOffset = Math.max(0, listRect.width - basePadding - parseFloat(styles.paddingRight) - 300);
    const offset = parentText.getBoundingClientRect().left - (listRect.left + basePadding);
    list.style.paddingLeft = `${basePadding + Math.min(Math.max(offset, 0), maxOffset)}px`;
  }

  onPointerEnter(event) {
    if (event.pointerType !== 'mouse') return;
    clearTimeout(this.closeTimeout);
    if (!this.mainDetailsToggle.open) this.openedByHover = true;
    this.open();
  }

  onPointerLeave(event) {
    if (event.pointerType !== 'mouse') return;
    clearTimeout(this.closeTimeout);
    this.closeTimeout = setTimeout(() => this.close(), 200);
  }

  onSummaryClick(event) {
    // A mouse click on a menu that hover just opened would otherwise toggle it shut under the cursor.
    if (event.detail === 0 || this.lastPointerType !== 'mouse' || !this.openedByHover) return;
    if (event.target.closest('.header__menu-parent-link')) return;
    this.openedByHover = false;
    event.preventDefault();
    // global.js flips aria-expanded on every summary click; restore it since the menu stays open.
    setTimeout(() => this.summary.setAttribute('aria-expanded', true));
  }

  onParentLinkClick(event) {
    const isTouch = this.lastPointerType === 'touch' || this.lastPointerType === 'pen';
    if (event.detail === 0 || !isTouch || this.mainDetailsToggle.open) return;
    event.preventDefault();
    // Keep the click from reaching the summary, which would flip aria-expanded back to false.
    event.stopPropagation();
    this.open();
  }

  open() {
    if (this.mainDetailsToggle.open) return;
    document.querySelectorAll('header-menu').forEach((menu) => {
      if (menu !== this && menu.mainDetailsToggle.open) menu.close();
    });
    this.mainDetailsToggle.setAttribute('open', '');
    this.summary.setAttribute('aria-expanded', true);
  }

  close() {
    this.openedByHover = false;
    super.close();
  }

  onToggle() {
    if (this.alignedList && this.mainDetailsToggle.open) this.alignUnderParent();
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);

